import React, { useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { formatNumber } from '../../i18n/format'
import { AppUseSelector } from '../../redux/hooks'
import {
  bulkSetWordMastery,
  bulkSetWordMasteryByFilter,
  listMyWords,
  listMyWordsStats,
  MasteryThree,
  prefetchPage,
  setSelectAllMatching,
  setWordsFilters,
  setWordsPage,
  setWordsSelection,
} from '../../redux/actions/wordsAction'
import { WordsLevelFilters } from '../../redux/reducers/wordsReducer'
import Loader from '../../containers/loader/loader'
import WordsHeader from './WordsHeader'
import WordsProgress from './WordsProgress'
import WordsFilters from './WordsFilters'
import WordRow from './WordRow'
import { dismissWordsCallout } from '../../utils/wordsDiscovery'
import { membershipNameType, PlanChangeLink } from '../../utils/constant'
import WeeklyOfferGate from '../weekly-offer/WeeklyOfferGate'
import './words.css'

// Free learners can browse the first MAX_FREE_PAGE pages of their word list.
// Beyond that, the Next button surfaces an upgrade tooltip (mirrors the
// disabled-microphone pattern in containers/player/Player.tsx). Server-side
// rate/scrape protection still applies to all users.
const MAX_FREE_PAGE = 20

// Labels are translation keys.
const bulkOptions: { value: MasteryThree; label: string }[] = [
  { value: 'unknown', label: 'words:mastery.unknown' },
  { value: 'learning', label: 'words:mastery.learning' },
  { value: 'mastered', label: 'words:mastery.known' },
]

const Words: React.FC = () => {
  const { t } = useTranslation('words')
  const { items, page, perPage, total, loading, filters, selectedIds, selectAllMatching } =
    AppUseSelector(({ words }) => words)
  const membership = AppUseSelector(({ user }) => user?.membership)
  const isFreeUser = membership?.name === membershipNameType.Free
  const totalPages = Math.ceil(total / perPage) || 1
  const navigate = useNavigate()
  const nextLockRef = useRef<HTMLDivElement>(null)
  const nextIsLocked = isFreeUser && page >= MAX_FREE_PAGE && totalPages > MAX_FREE_PAGE

  const masteryParam = filters.mastery.length ? filters.mastery : undefined
  const searchParam = filters.search || undefined
  const levelsParam: WordsLevelFilters = filters.levels
  const strugglingParam = filters.struggling || undefined
  const masteryKey = filters.mastery.join(',')
  const levelsKey = `${filters.levels.hsk.join(',')}|${filters.levels.newHsk.join(',')}|${filters.levels.tofcl.join(',')}`

  // The Words page being mounted is itself a "discovery" event — clear the
  // story-finished callout flag so we don't keep nudging users who already
  // found this feature on their own (e.g. via the header nav link).
  useEffect(() => {
    dismissWordsCallout()
  }, [])

  // Initial fetch + refetch on filter change. Always resets to page 1 because
  // a filter change invalidates the current pagination position.
  useEffect(() => {
    listMyWords({
      page: 1,
      perPage,
      search: searchParam,
      mastery: masteryParam,
      levels: levelsParam,
      struggling: strugglingParam,
    })
    setWordsPage(1)
  }, [filters.search, masteryKey, levelsKey, filters.struggling, perPage]) // eslint-disable-line

  // Stats (progress bar) refetch on search + level changes, but NOT mastery —
  // toggling mastery chips should never flicker the progress numbers.
  useEffect(() => {
    listMyWordsStats({ search: searchParam, levels: levelsParam })
  }, [filters.search, levelsKey]) // eslint-disable-line

  // Prefetch neighbours so Next/Prev feels instant.
  useEffect(() => {
    if (loading) return
    const base = {
      perPage,
      search: searchParam,
      mastery: masteryParam,
      levels: levelsParam,
      struggling: strugglingParam,
    }
    if (page < totalPages) prefetchPage({ ...base, page: page + 1 })
    if (page > 1) prefetchPage({ ...base, page: page - 1 })
  }, [page, totalPages, perPage, filters.search, masteryKey, levelsKey, filters.struggling, loading]) // eslint-disable-line

  const goToPage = (next: number) => {
    if (next < 1 || next > totalPages || next === page) return
    if (isFreeUser && next > MAX_FREE_PAGE) {
      // Safety net — Next button is already rendered as the upgrade lock when
      // we'd cross this boundary, but a stale call from Prev/elsewhere could
      // still land here. Surface the tooltip and stay put.
      nextLockRef.current?.focus()
      return
    }
    setWordsPage(next)
    // Kick off a fetch — if cached, this is a no-op visually because the reducer
    // already populated items from cache; the request still runs to refresh.
    listMyWords({
      page: next,
      perPage,
      search: searchParam,
      mastery: masteryParam,
      levels: levelsParam,
      struggling: strugglingParam,
    })
  }

  const handleUpgradeClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(PlanChangeLink)
  }

  const handleLockClose = (e: React.MouseEvent) => {
    e.stopPropagation()
    nextLockRef.current?.blur()
  }

  const handleSearchChange = (search: string) => setWordsFilters({ search })
  const handleMasteryChange = (mastery: string[]) => setWordsFilters({ mastery })
  const handleLevelsChange = (levels: Partial<WordsLevelFilters>) => setWordsFilters({ levels })
  const handleStrugglingChange = (struggling: boolean) => setWordsFilters({ struggling })

  const allSelected = useMemo(
    () => items.length > 0 && items.every((it) => selectedIds.includes(it.wordId)),
    [items, selectedIds],
  )
  const someSelected = selectedIds.length > 0 && !allSelected

  // Whether the user has narrowed the list with a filter — "select all matching"
  // is only offered for filtered views (mirrors the request: mark a whole level).
  const hasActiveFilter =
    !!filters.search ||
    filters.struggling ||
    filters.mastery.length > 0 ||
    filters.levels.hsk.length > 0 ||
    filters.levels.newHsk.length > 0 ||
    filters.levels.tofcl.length > 0

  // Offer "select all N matching" once the whole page is selected and there are
  // more matching words beyond this page.
  const canSelectAllMatching =
    hasActiveFilter && (allSelected || selectAllMatching) && total > items.length

  const toggleSelectAll = () => {
    if (selectAllMatching) {
      setWordsSelection([])
      return
    }
    if (allSelected) {
      const pageIds = new Set(items.map((it) => it.wordId))
      setWordsSelection(selectedIds.filter((id) => !pageIds.has(id)))
    } else {
      const merged = Array.from(new Set([...selectedIds, ...items.map((it) => it.wordId)]))
      setWordsSelection(merged)
    }
  }

  const toggleOne = (wordId: string) => {
    setWordsSelection(
      selectedIds.includes(wordId)
        ? selectedIds.filter((id) => id !== wordId)
        : [...selectedIds, wordId],
    )
  }

  const handleBulk = async (m: MasteryThree) => {
    if (selectAllMatching) {
      try {
        await bulkSetWordMasteryByFilter(filters, m)
      } catch {
        // refetch/revert handled inside the action
      }
      return
    }
    if (!selectedIds.length) return
    try {
      await bulkSetWordMastery(selectedIds, m)
    } catch {
      // optimistic revert handled inside the action
    }
  }

  const clearSelection = () => setWordsSelection([])

  const selectedCount = selectAllMatching ? total : selectedIds.length

  return (
    <div className='words-root'>
      <WeeklyOfferGate placement='words' />
      <WordsHeader />
      <WordsProgress />
      <WordsFilters
        search={filters.search}
        mastery={filters.mastery}
        levels={filters.levels}
        struggling={filters.struggling}
        onSearchChange={handleSearchChange}
        onMasteryChange={handleMasteryChange}
        onLevelsChange={handleLevelsChange}
        onStrugglingChange={handleStrugglingChange}
      />

      {loading && (
        <div className='words-loading'>
          <Loader />
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className='words-empty'>
          {filters.struggling && !filters.search && !filters.mastery.length
            ? t('list.emptyStruggling')
            : filters.search || filters.mastery.length || filters.struggling
              ? t('list.emptyFiltered')
              : t('list.empty')}
        </div>
      )}

      {!loading && items.length > 0 && (
        <>
          <div className='words-bulk-bar'>
            <div className='words-bulk-selectinfo'>
              <label className='words-bulk-select-all'>
                <input
                  type='checkbox'
                  checked={allSelected || selectAllMatching}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !selectAllMatching
                  }}
                  onChange={toggleSelectAll}
                  aria-label={t('list.selectAllOnPage')}
                />
                <span>
                  {selectedCount ? t('list.selected', { count: selectedCount }) : t('list.selectPage')}
                </span>
              </label>

              {canSelectAllMatching &&
                (selectAllMatching ? (
                  <button
                    type='button'
                    className='words-bulk-selectall-link'
                    onClick={() => setSelectAllMatching(false)}
                  >
                    {t('list.selectOnlyPage')}
                  </button>
                ) : (
                  <button
                    type='button'
                    className='words-bulk-selectall-link'
                    onClick={() => setSelectAllMatching(true)}
                  >
                    {t('list.selectAllMatching', { total: formatNumber(total) })}
                  </button>
                ))}
            </div>

            {selectedCount > 0 && (
              <div className='words-bulk-actions'>
                <span className='words-bulk-label'>{t('list.setTo')}</span>
                {bulkOptions.map((opt) => (
                  <button
                    key={opt.value}
                    className='words-bulk-btn'
                    onClick={() => handleBulk(opt.value)}
                  >
                    {t(opt.label)}
                  </button>
                ))}
                <button className='words-bulk-clear' onClick={clearSelection}>
                  {t('list.clear')}
                </button>
              </div>
            )}
          </div>

          <div className='words-list'>
            {items.map((item) => (
              <WordRow
                key={item.wordId}
                item={item}
                selected={selectedIds.includes(item.wordId)}
                onToggleSelect={() => toggleOne(item.wordId)}
              />
            ))}
          </div>
          <div className='words-pagination'>
            <button disabled={page <= 1} onClick={() => goToPage(page - 1)}>
              {t('list.prev')}
            </button>
            <span>{t('list.pageOf', { page: String(page), totalPages: String(totalPages) })}</span>
            {nextIsLocked ? (
              <div
                ref={nextLockRef}
                className='words-next-lock'
                tabIndex={0}
                role='button'
                aria-label={t('list.nextLocked')}
                onClick={() => nextLockRef.current?.focus()}
              >
                <span>
                  {t('list.next')} <i className='fa fa-lock' aria-hidden='true' />
                </span>
                <div className='words-next-lock-tooltip'>
                  <button
                    className='words-next-lock-close'
                    onClick={handleLockClose}
                    aria-label={t('list.dismiss')}
                  >
                    <i className='fa fa-times' aria-hidden='true' />
                  </button>
                  <Trans
                    t={t}
                    i18nKey='list.freeLimit'
                    values={{ pages: String(MAX_FREE_PAGE) }}
                    components={{ upgrade: <a onClick={handleUpgradeClick} /> }}
                  />
                </div>
              </div>
            ) : (
              <button disabled={page >= totalPages} onClick={() => goToPage(page + 1)}>
                {t('list.next')}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default Words
